import type { ApiBackend, ApiErrorClassification, ApiErrorDetails } from "@/api/api-client-error.ts";

import { isAxiosError } from "axios";
import { z } from "zod";

import { zApiError } from "@/.generated/hey/backoffice-backend/zod.gen.ts";
import { zErrorResponse } from "@/.generated/hey/subscriber-backend/zod.gen.ts";
import { ApiClientError } from "@/api/api-client-error.ts";
import { backOfficeErrorSchema, kiliErrorSchema } from "@/api/api-error.type.ts";

export type AxiosBackend = Extract<ApiBackend, "api-gouv" | "backoffice" | "kili">;
export type HeyApiBackend = Extract<ApiBackend, "backoffice-v2" | "subscriber-backend">;

interface ErrorContext {
  error: unknown;
  status?: number;
  method?: string;
  url?: string;
  request?: Request;
  response?: Response;
}

interface NormalizedPayload {
  classification: ApiErrorClassification;
  details?: ApiErrorDetails;
}

interface ApiErrorNormalizationStrategy {
  normalize(error: unknown, status?: number): NormalizedPayload;
}

const relaxedBackofficeErrorSchema = zApiError.extend({ messageCode: z.string().optional() });
const googleMapsErrorSchema = z.object({
  code: z.string().optional(),
  endpoint: z.string().optional(),
  message: z.string().optional(),
});

const strategies = {
  backoffice: backofficeErrorStrategy("backoffice"),
  "backoffice-v2": backofficeErrorStrategy("backoffice-v2"),
  kili: categorizedErrorStrategy("kili", kiliErrorSchema),
  "subscriber-backend": categorizedErrorStrategy("subscriber-backend", zErrorResponse),
  "api-gouv": {
    normalize(_error, status) {
      return {
        classification: classifyHttpStatus(status, "unknown"),
        details: { backend: "api-gouv" },
      };
    },
  },
  "google-maps": {
    normalize(error) {
      const parsedError = googleMapsErrorSchema.safeParse(error);
      if (!parsedError.success) {
        return { classification: "unknown", details: { backend: "google-maps" } };
      }

      return {
        classification: parsedError.data.code === "ZERO_RESULTS" ? "expected-status" : "technical",
        details: {
          backend: "google-maps",
          code: parsedError.data.code,
          endpoint: parsedError.data.endpoint,
          message: parsedError.data.message,
        },
      };
    },
  },
} satisfies Record<ApiBackend, ApiErrorNormalizationStrategy>;

export function normalizeAxiosError(error: unknown, backend: AxiosBackend): Error {
  if (error instanceof ApiClientError) {
    return error;
  }

  if (!isAxiosError(error)) {
    return normalizeApiError({ error }, backend);
  }

  return normalizeApiError(
    {
      error: error.response?.data ?? error,
      status: error.response?.status,
      method: error.config?.method?.toUpperCase(),
      url: buildAxiosUrl(error.config?.baseURL, error.config?.url),
    },
    backend,
    error,
  );
}

export function normalizeHeyApiError(context: ErrorContext, backend: HeyApiBackend): ApiClientError {
  return normalizeApiError(context, backend);
}

export function normalizeGoogleMapsError(error: unknown): ApiClientError {
  return normalizeApiError({ error }, "google-maps", error instanceof Error ? error : undefined);
}

function normalizeApiError(context: ErrorContext, backend: ApiBackend, originalError?: Error): ApiClientError {
  const normalizedPayload = strategies[backend].normalize(context.error, context.status);

  return new ApiClientError({
    backend,
    classification: normalizedPayload.classification,
    status: context.status,
    method: context.method,
    url: context.url,
    request: context.request,
    response: context.response,
    details: normalizedPayload.details,
    originalError: originalError ?? (context.error instanceof Error ? context.error : undefined),
  });
}

function backofficeErrorStrategy(backend: "backoffice" | "backoffice-v2"): ApiErrorNormalizationStrategy {
  return {
    normalize(error, status) {
      const parsedError = backOfficeErrorSchema.or(relaxedBackofficeErrorSchema).safeParse(error);
      return {
        classification: classifyHttpStatus(status, status === 400 && parsedError.success ? "domain" : "unknown"),
        details: parsedError.success
          ? {
              backend,
              code: parsedError.data.code ?? undefined,
              message: parsedError.data.message ?? undefined,
              messageCode: parsedError.data.messageCode ?? undefined,
            }
          : undefined,
      };
    },
  };
}

function categorizedErrorStrategy(
  backend: "kili" | "subscriber-backend",
  schema: typeof kiliErrorSchema | typeof zErrorResponse,
): ApiErrorNormalizationStrategy {
  return {
    normalize(error, status) {
      const parsedError = schema.safeParse(error);
      if (!parsedError.success) {
        return { classification: classifyHttpStatus(status, "unknown") };
      }

      const details = parsedError.data.errorDetails;
      return {
        classification: classifyHttpStatus(status, classifyErrorCategory(parsedError.data.errorCategory)),
        details: {
          backend,
          errorCategory: parsedError.data.errorCategory,
          errorCode: "errorCode" in details ? (details.errorCode ?? undefined) : undefined,
          message: "message" in details ? (details.message ?? undefined) : undefined,
          fieldErrors: "fieldErrors" in details ? details.fieldErrors : undefined,
        },
      };
    },
  };
}

function classifyHttpStatus(
  status: number | undefined,
  parsedClassification: ApiErrorClassification,
): ApiErrorClassification {
  if (status === 401 || status === 403) {
    return "authentication";
  }
  if (status === 429 || (status !== undefined && status >= 500)) {
    return "technical";
  }
  return parsedClassification;
}

function classifyErrorCategory(errorCategory: string): ApiErrorClassification {
  if (errorCategory === "DOMAIN_ERROR") {
    return "domain";
  }
  if (errorCategory === "FORM_VALIDATION_ERROR") {
    return "form-validation";
  }
  if (errorCategory === "TECHNICAL_ERROR" || errorCategory === "UNEXPECTED_ERROR") {
    return "technical";
  }
  return "unknown";
}

function buildAxiosUrl(baseUrl?: string, path?: string): string | undefined {
  if (!path) {
    return baseUrl;
  }
  if (!baseUrl || /^https?:\/\//.test(path)) {
    return path;
  }
  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}
